import { useCallback, useState } from 'react';
import {
  signInWithEmailAndPassword,
  OAuthProvider,
  signInWithPopup,
  signOut,
  unlink,
  linkWithPopup,
  AuthErrorCodes,
} from 'firebase/auth';
import { useMutation } from 'react-query';
import { auth } from 'firebaseConfig';
import { useToast } from 'hooks/useToast';
import { useUserSession } from 'hooks/useUserSession';
import { EVENT_NAMES } from 'services/analytics/eventNames';
import { trackEvent } from 'services/analytics/trackEvent';
import UserAPI, { ERROR_CODES_VALIDATE_TOKEN } from 'services/API/UserAPI';
import { useUser } from 'state/auth';
import { trackLogin } from 'utils/analytics';
import { LoginPageViewSections, LoginSuccessStates } from 'utils/analytics/constants';
import { trackLinkWithSSO, trackLoginWithSSO } from 'utils/analytics/ssoAuth';
import { SSO_PROVIDERS } from 'utils/constants/ssoProviders';

export const useAuth = () => {
  const { renderToast } = useToast();
  const { user } = useUser();
  const { initializeUserSession, resetUserSession } = useUserSession();
  const { setToken, setRefreshToken } = useUser();

  const getLoginErrorMessage = useCallback((responseStatus: number) => {
    switch (responseStatus) {
      case 402:
        return {
          title: 'Account Error',
          message: 'Your account is no longer active',
        };
      case 403:
      default:
        return { title: 'Error', message: 'Invalid credentials' };
    }
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      try {
        const result = await signInWithEmailAndPassword(auth, email, password);

        const accessToken = await result.user.getIdToken();

        await initializeUserSession(accessToken, result.user.refreshToken);
      } catch (e: any) {
        const { message, title } = getLoginErrorMessage(e?.response?.status);
        renderToast({
          toastTitle: 'failed_login',
          title,
          label: message,
          status: 'error',
        });
        trackLogin({
          successState: LoginSuccessStates.FAILURE,
        });
      }
    },
    [renderToast, initializeUserSession],
  );

  const loginWithMicrosoftSSO = useCallback(async () => {
    try {
      const provider = new OAuthProvider('microsoft.com');
      const result = await signInWithPopup(auth, provider);

      const accessToken = await result.user.getIdToken();

      await initializeUserSession(accessToken, result.user.refreshToken);
      trackLoginWithSSO({
        clickAction: 'success',
        provider: SSO_PROVIDERS.MICROSOFT,
        sectionName: LoginPageViewSections.LOGIN,
      });
    } catch (error: any) {
      const errorMessage = 'Invalid credentials';

      const errorResponseMessage = error?.response?.data?.message;

      if (errorResponseMessage === ERROR_CODES_VALIDATE_TOKEN.INCOMPLETE_SIGNUP) {
        renderToast({
          toastTitle: 'failed_login',
          title: 'Account incomplete',
          label:
            'Follow the invite code in your email to finish setting up your account.',
          status: 'error',
        });
      } else if (error?.code === AuthErrorCodes.NEED_CONFIRMATION) {
        renderToast({
          toastTitle: 'failed_login',
          title: 'Account already exists',
          label:
            'Please log in with your password, then link your account in Account Settings.',
          status: 'error',
        });
      } else {
        renderToast({
          toastTitle: 'failed_login',
          title: 'Error',
          label: errorMessage,
          status: 'error',
        });
      }
      trackLogin({
        successState: LoginSuccessStates.FAILURE,
      });
      trackLoginWithSSO({
        clickAction: 'failure',
        provider: SSO_PROVIDERS.MICROSOFT,
      });
    }
  }, [renderToast, initializeUserSession, setToken, setRefreshToken]);

  const logout = useCallback(() => {
    trackEvent(EVENT_NAMES.LOGOUT, {
      userId: user?.id,
      companyId: user?.company_id,
    });
    signOut(auth);
    resetUserSession();
  }, [resetUserSession, user?.company_id, user?.id]);

  return {
    login,
    logout,
    loginWithMicrosoftSSO,
  };
};

interface RegisterInvitedUserPayload {
  inviteCode: string;
  email: string;
  firstName: string;
  lastName: string;
  jobTitle: string;
  password: string;
}

export const useNewUserRegistration = () => {
  const { login } = useAuth();
  const { renderToast } = useToast();
  const { initializeUserSession } = useUserSession();

  const registerInvitedUser = useMutation({
    mutationFn: async ({
      inviteCode,
      email,
      firstName,
      lastName,
      jobTitle,
      password,
    }: RegisterInvitedUserPayload) => {
      try {
        await UserAPI.postRegisterInvitedUser({
          inviteCode,
          firstName,
          lastName,
          jobTitle,
          password,
        });
        await login(email, password);
        renderToast({
          toastTitle: 'success_invite_registration',
          title: 'Success',
          label: 'Account created successfully',
        });
      } catch (_) {
        renderToast({
          toastTitle: 'failed_invite_registration',
          title: 'Error',
          label: 'Account creation failed',
          status: 'error',
        });
      }
    },
  });

  const registerInvitedUserWithMicrosoftSSO = useMutation({
    mutationFn: async ({
      inviteCode,
      accessToken,
      refreshToken,
      firstName,
      lastName,
      jobTitle,
    }: {
      accessToken: string;
      refreshToken: string;
      inviteCode: string;
      firstName: string;
      lastName: string;
      jobTitle: string;
    }) => {
      await UserAPI.postRegisterInvitedUser({
        inviteCode,
        authToken: accessToken,
        firstName,
        lastName,
        jobTitle,
      });

      await initializeUserSession(accessToken, refreshToken);

      renderToast({
        toastTitle: 'success_invite_registration',
        title: 'Success',
        label: 'Account created successfully',
      });
    },
  });

  return { registerInvitedUser, registerInvitedUserWithMicrosoftSSO };
};

export const useLinkAccountWithSSO = () => {
  const { renderToast } = useToast();
  const [isLinking, setIsLinking] = useState<boolean>(false);

  const linkAccountWithMicrosoft = async () => {
    if (isLinking || !auth.currentUser?.email) return;

    try {
      setIsLinking(true);
      const provider = new OAuthProvider('microsoft.com');

      const result = await linkWithPopup(auth.currentUser, provider);

      // Provider info for Firebase user
      const { providerData } = result.user;

      // Email already on the Firebase user record
      const existingEmail = auth.currentUser.email;

      const microsoftLinkedInfo = providerData.find(
        (linkedProvider) => linkedProvider.providerId === provider.providerId,
      );
      if (
        microsoftLinkedInfo &&
        microsoftLinkedInfo.email?.toLowerCase() !== existingEmail.toLowerCase()
      ) {
        // Undo the link if emails don't match
        await unlink(auth.currentUser, provider.providerId);
        throw new Error('Microsoft email does not match your Mercator account.');
      }

      renderToast({
        toastTitle: 'success_microsoft_account_linking',
        title: 'Success',
        label: 'Your account has been successfully linked with Microsoft SSO',
        status: 'success',
      });
      trackLinkWithSSO({
        clickAction: 'success',
        provider: SSO_PROVIDERS.MICROSOFT,
      });
    } catch (error: any) {
      trackLinkWithSSO({
        clickAction: 'failure',
        provider: SSO_PROVIDERS.MICROSOFT,
      });
      let errorMessage = 'Failed to link account with Microsoft SSO. Please try again.';

      // Handle specific Firebase auth errors
      if (error.code === AuthErrorCodes.PROVIDER_ALREADY_LINKED) {
        errorMessage =
          'This Microsoft account is already linked to another user account.';
      } else if (error.code === AuthErrorCodes.CREDENTIAL_ALREADY_IN_USE) {
        errorMessage = 'This Microsoft account is already associated with another user.';
      } else if (error.code === AuthErrorCodes.CREDENTIAL_TOO_OLD_LOGIN_AGAIN) {
        errorMessage = 'Please log in again before linking your account.';
      }

      renderToast({
        toastTitle: 'fail_microsoft_account_linking',
        title: 'Error',
        label: errorMessage,
        status: 'error',
      });
    } finally {
      setIsLinking(false);
    }
  };

  return {
    linkAccountWithMicrosoft,
    isLinking,
  };
};
